// Typed view of data/release-notes.json, the one source for release notes.
// The site renders /changelog and /changelog/<slug> from it, and every deploy
// publishes the same JSON at https://workspace369.com/release-notes.json for
// the Flutter app's Settings -> What's new screen (scripts/export-release-notes.mjs).
// Edit the JSON, never a copy. Rules: docs/release-notes.md.
import source from './release-notes.json';

export interface ReleaseImage {
  src: string;
  width: number;
  height: number;
  alt: string;
}

export interface ReleaseLink {
  label: string;
  href: string;
}

export interface ReleaseVideo {
  /** planned = no video yet; unlisted/public = youtubeId is set. */
  status: 'planned' | 'unlisted' | 'public';
  youtubeId: string | null;
  title: string;
  /** ISO 8601 duration as YouTube reports it, e.g. PT1M12S. */
  duration: string | null;
  /** ISO date the video was uploaded. */
  uploadDate: string | null;
}

export interface ReleaseFeature {
  id: string;
  title: string;
  body: string[];
  highlights: string[];
  /** Desktop or tablet render of the real app. */
  image: ReleaseImage | null;
  /** Phone render (390pt at 125% text) shown on small screens and in the phone app. */
  imagePhone: ReleaseImage | null;
  whereToFind: string;
  appRoute: string | null;
  /** "all" or the plan names that include it, cheapest first. */
  plans: 'all' | string[];
  links: ReleaseLink[];
}

export interface Release {
  slug: string;
  date: string;
  /** YYYY-MM-DD, only when the entry was amended in substance after it shipped (dateModified, sitemap lastmod, feed). */
  updated?: string;
  title: string;
  /** Short title for <title> and search results (≤ 45 chars; " | Workspace369" is appended). */
  seoTitle: string;
  summary: string;
  areas: string[];
  hero: ReleaseImage;
  video: ReleaseVideo;
  features: ReleaseFeature[];
}

export const releaseAreas: Record<string, string> = source.areas;

export const releases: Release[] = [...(source.releases as Release[])].sort((a, b) =>
  b.date.localeCompare(a.date),
);

export const releasesBySlug: Record<string, Release> = Object.fromEntries(
  releases.map((release) => [release.slug, release]),
);

export const releasePath = (release: Pick<Release, 'slug'>) => `/changelog/${release.slug}`;

/** The <title> of a release page. */
export const releaseMetaTitle = (release: Pick<Release, 'seoTitle'>) => `${release.seoTitle} | Workspace369`;

// 800w/1200w copies sit next to each hero.webp (scripts/release-note-hero-variants.mjs).
export const heroSrcset = (hero: ReleaseImage) =>
  hero.src.endsWith('/hero.webp')
    ? [800, 1200].map((w) => `${hero.src.replace(/hero\.webp$/, `hero-${w}.webp`)} ${w}w`).concat(`${hero.src} ${hero.width}w`).join(', ')
    : undefined;

export const hasVideo = (release: Release) =>
  release.video.status !== 'planned' && Boolean(release.video.youtubeId);

export const formatReleaseDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const planLabel = (plans: ReleaseFeature['plans']) =>
  plans === 'all' ? 'Every plan' : plans.length === 1 ? `${plans[0]}` : `${plans[0]} and up`;

/** Releases with a feature that links to this site path (newest first). */
export const releasesLinkingTo = (path: string) => {
  const normalized = path.replace(/\/+$/, '') || '/';
  return releases.filter((release) =>
    release.features.some((feature) =>
      feature.links.some((link) => link.href.replace(/\/+$/, '') === normalized),
    ),
  );
};
